import { type WritableDraft } from '@reduxjs/toolkit';
import md5 from 'md5';
import { type RuleTopLevelCondition } from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { type DateString } from '@bbkAdminRedux/rtkq/current-user.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

type Pagination = {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_more: boolean;
};

export enum OptimizerEntityType {
  OPTIMIZATION = 'OPTIMIZATION',
}

export enum OptimizationActionType {
  PLAN_CHANGE = 'PLAN_CHANGE',
  PLAN_PRICE_ADJUSTMENT = 'PLAN_PRICE_ADJUSTMENT',
  ADDON_PRICE_ADJUSTMENT = 'ADDON_PRICE_ADJUSTMENT',
  ALIGN_TO_CATALOG = 'ALIGN_TO_CATALOG',
}

export enum OptimizerJobType {
  EXPORT = 'EXPORT',
  SIMULATION = 'SIMULATION',
  EXECUTION = 'EXECUTION',
}

export enum OptimizerJobStatus {
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
  RUNNABLE = 'RUNNABLE',
  STARTING = 'STARTING',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type OptimizerJobStatusCategory = 'in_progress' | 'success' | 'error';

/**
 * Categorizes a job status into: in_progress, success, or error
 * - In progress: SUBMITTED, PENDING, RUNNABLE, STARTING, RUNNING
 * - Success: SUCCEEDED
 * - Error: FAILED, CANCELLED
 */
export const getOptimizerJobStatusCategory = (
  status: EnumLike<typeof OptimizerJobStatus> | undefined
): OptimizerJobStatusCategory | undefined => {
  if (!status) return undefined;

  switch (status) {
    case OptimizerJobStatus.SUBMITTED:
    case OptimizerJobStatus.PENDING:
    case OptimizerJobStatus.RUNNABLE:
    case OptimizerJobStatus.STARTING:
    case OptimizerJobStatus.RUNNING:
      return 'in_progress';
    case OptimizerJobStatus.SUCCEEDED:
      return 'success';
    case OptimizerJobStatus.FAILED:
    case OptimizerJobStatus.CANCELLED:
      return 'error';
    default:
      return undefined;
  }
};

type AbstractOptimizationAction = {
  type: EnumLike<typeof OptimizationActionType>;
  display_name: string;
  condition: RuleTopLevelCondition;
};

export enum OptimizationPriceAdjustmentType {
  INCREASE_BY_PERCENTAGE = 'INCREASE_BY_PERCENTAGE',
  INCREASE_BY_FIXED_AMOUNT = 'INCREASE_BY_FIXED_AMOUNT',
  DECREASE_BY_PERCENTAGE = 'DECREASE_BY_PERCENTAGE',
  DECREASE_BY_FIXED_AMOUNT = 'DECREASE_BY_FIXED_AMOUNT',
  SET_FIXED_PRICE = 'SET_FIXED_PRICE',
}

export enum OptimizationPlanScope {
  ALL_PLANS = 'ALL_PLANS',
  SPECIFIC_PLANS = 'SPECIFIC_PLANS',
}

export enum OptimizationAddonScope {
  ALL_ADDONS = 'ALL_ADDONS',
  SPECIFIC_ADDONS = 'SPECIFIC_ADDONS',
}

export enum OptimizationAlignToCatalogScope {
  ALL_ITEMS = 'ALL_ITEMS',
  SPECIFIC_ITEMS = 'SPECIFIC_ITEMS',
}

export enum OptimizationAddonAdjustmentType {
  INCREASE_BY_PERCENTAGE = 'INCREASE_BY_PERCENTAGE',
  INCREASE_BY_FIXED_AMOUNT = 'INCREASE_BY_FIXED_AMOUNT',
  DECREASE_BY_PERCENTAGE = 'DECREASE_BY_PERCENTAGE',
  DECREASE_BY_FIXED_AMOUNT = 'DECREASE_BY_FIXED_AMOUNT',
  SET_FIXED_PRICE = 'SET_FIXED_PRICE',
}

type OptimizationActionPriceAdjustment = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.PLAN_PRICE_ADJUSTMENT>;
  scope?: EnumLike<typeof OptimizationPlanScope>;
  adjustment_type?: EnumLike<typeof OptimizationPriceAdjustmentType>;
  adjustment_value?: number;
  /** ISO 4217 for ALL_PLANS fixed amounts; server converts major→minor using ISO digits. */
  currency_code?: string | null;
  plan_adjustments?: OptimizationPlanAdjustment[];
};

export type OptimizationPlanAdjustment = {
  plan_id: string;
  adjustment_type: EnumLike<typeof OptimizationPriceAdjustmentType>;
  adjustment_value: number;
  /** ISO 4217 for this row’s fixed amounts (from catalog). */
  currency_code?: string | null;
};

export type OptimizationAddonAdjustment = {
  item_price_id: string;
  adjustment_type: EnumLike<typeof OptimizationAddonAdjustmentType>;
  adjustment_value: number;
  /** ISO 4217 for this row’s fixed amounts (from catalog). */
  currency_code?: string | null;
};

type OptimizationActionAddonPriceAdjustment = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.ADDON_PRICE_ADJUSTMENT>;
  scope: EnumLike<typeof OptimizationAddonScope>;
  adjustment_type?: EnumLike<typeof OptimizationAddonAdjustmentType>;
  adjustment_value?: number;
  /** ISO 4217 for ALL_ADDONS fixed amounts; server converts major→minor using ISO digits. */
  currency_code?: string | null;
  addons?: OptimizationAddonAdjustment[];
};

type OptimizationActionPlanChange = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.PLAN_CHANGE>;
  plans: {
    current_plan: string;
    target_plan: string;
  }[];
};

type OptimizationActionAlignToCatalog = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.ALIGN_TO_CATALOG>;
  scope: EnumLike<typeof OptimizationAlignToCatalogScope>;
  item_price_ids?: string[];
};

export type OptimizationAction =
  | OptimizationActionPriceAdjustment
  | OptimizationActionPlanChange
  | OptimizationActionAddonPriceAdjustment
  | OptimizationActionAlignToCatalog;

export enum ApplyChangesEnum {
  IMMEDIATELY = 'immediately',
  END_OF_TERM = 'end_of_term',
}

export enum MidCycleBillingEnum {
  DISABLE_PRORATION = 'disable_proration',
  ENABLE_PRORATION = 'enable_proration',
}

export enum ScheduledChangesHandlingEnum {
  SKIP_CONFLICTING_SUBSCRIBERS = 'skip_conflicting_subscribers',
  DISCARD_EXISTING_SCHEDULES = 'discard_existing_schedules',
  RETAIN_EXISTING_SCHEDULES = 'retain_existing_schedules',
}

export type OptimizationConfigurations = {
  apply_changes?: ApplyChangesEnum;
  mid_cycle_billing?: MidCycleBillingEnum;
  scheduled_changes_handling?: ScheduledChangesHandlingEnum;
};

export type CustomizableOptimization = {
  display_name: string;
  audience?: RuleTopLevelCondition;
  actions?: OptimizationAction[];
  configurations?: OptimizationConfigurations;
  active?: boolean;
};

export type Optimization = {
  type: OptimizerEntityType.OPTIMIZATION;
  version: string;
  app_key: string;
  opt_id: string;
  created_at: DateString;
  updated_at: DateString;
  active: boolean;
  status?: string;
} & CustomizableOptimization;

export type OptimizationListResponse = {
  collection: Optimization[];
};

export type CreateOptimizationResponse = { ok: boolean } & Pick<
  Optimization,
  'app_key' | 'opt_id'
>;

export type DeleteOptimizationResponse = {
  ok: boolean;
} & Pick<Optimization, 'app_key' | 'opt_id'>;

type UpdateOptimizationResponse = { ok: boolean } & Optimization;

export type SubscriptionItem = {
  item_price_id: string;
  item_type: string;
  amount: number;
  quantity: number;
  billing_period: number;
  billing_cycles: number;
  unit_price?: number;
  free_quantity?: number;
  currency?: string;
  currency_code?: string;
  [key: string]: unknown;
};

export type SubscriptionWithChanges = {
  subscription_id: string;
  customer_id: string;
  customer: ChargebeeCustomer; // Customer data is now included directly in the subscription response
  current_subscription_items: SubscriptionItem[];
  [key: string]: unknown;
};

export type ChargebeeCustomer = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  [key: string]: unknown;
};

export type GetSubscriptionsWithChangesResponse = {
  subscriptions: SubscriptionWithChanges[];
};

export type GetCustomersForSubscriptionsResponse = {
  customers: Record<string, ChargebeeCustomer>;
};

export type GetChangesForSubscriptionResponse = {
  changes_scheduled_at: number;
  billing_alignment:
    | 'immediately'
    | 'end_of_term'
    | 'specific_date'
    | 'billing_cycles';
  scheduled_subscription_items: SubscriptionItem[];
};

export type PeriodUnit = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

/** Chargebee plan item price row from optimizations-chargebee/plans. */
export type OptimizerChargebeePlan = {
  id: string;
  external_name: string; // short display name
  name: string; // full display name, includes: id + currency + frequency
  currency_code: string;
  period: number;
  period_unit: PeriodUnit;
  /** Integer minor units per ISO 4217 when multi-decimal strings are absent. */
  price: number;
  /** Major units from Chargebee when multi-decimal pricing is enabled; preferred over {@link price}. */
  price_in_decimal?: string | null;
  /**
   * Chargebee catalog fractional precision (multi-decimal). Used only to widen Intl
   * minimum/maximum fraction digits in the optimizer UI; job/API money integers stay ISO-minor.
   */
  decimal_places?: number | null;
};

export type GetPlansResponse = {
  total: number;
  collection: OptimizerChargebeePlan[];
};

export type Addon = {
  id: string;
  external_name: string;
  name: string;
  currency_code: string;
  period: number;
  period_unit: PeriodUnit;
  price: number;
  /** Major units from Chargebee when multi-decimal pricing is enabled; preferred over {@link price}. */
  price_in_decimal?: string | null;
  decimal_places?: number | null;
};

export type GetAddonsResponse = {
  total: number;
  collection: Addon[];
};

/** One row from simulation CSV item_price_changes (plan or addon line items). */
export type ItemPriceLineSnapshot = {
  type: string;
  item_price_id: string;
  quantity?: number;
  unit_price?: number;
  /** Major units from Chargebee when multi-decimal pricing is enabled; preferred over {@link unit_price}. */
  unit_price_in_decimal?: string | null;
  currency?: string;
  display_name?: string;
};

export type ItemPriceTierChangeRow = {
  start_unit: number;
  end_unit: number | null;
  current_unit_price: number;
  new_unit_price: number;
  current_unit_price_in_decimal?: string | null;
  new_unit_price_in_decimal?: string | null;
  currency_code: string;
};

export type ItemPriceChangeRow = {
  item_original: ItemPriceLineSnapshot;
  item_modified: ItemPriceLineSnapshot;
  pricing_model: string;
  reason: string | null;
  unit_label?: string | null;
  tier_changes: ItemPriceTierChangeRow[];
};

export type OptimizedSubscription = {
  subscription_id: string;
  customer_id: string;
  customer_first_name: string;
  customer_last_name: string;
  plan_id: string;
  /**
   * Legacy v1 fallback when `item_price_changes` is absent. Prefer `item_price_changes` (PLAN row)
   * when present.
   */
  original_item_price_id?: string | null;
  /**
   * Legacy v1 fallback when `item_price_changes` is absent. Prefer `item_price_changes` (PLAN row)
   * when present.
   */
  new_item_price_id?: string | null;
  subscription_started_at: number;
  has_scheduled_changes: boolean;
  changes_scheduled_at?: number | null;
  status: string;
  currency: string;
  auto_collection: boolean | null;
  apply_changes: ApplyChangesEnum;
  /**
   * Legacy v1 fallback when `item_price_changes` is absent. Prefer `item_price_changes` (PLAN row)
   * when present.
   */
  original_item_price?: number | null;
  /**
   * Legacy v1 fallback when `item_price_changes` is absent. Prefer `item_price_changes` (PLAN row)
   * when present.
   */
  new_item_price?: number | null;
  update_effective_date: string;
  billing_frequency: string;
  original_arr: number;
  new_arr: number;
  sub_plan_unit_price: number; // fallback price when original/new item prices are null
  sub_plan_quantity: number; // quantity for the subscription
  // Anticipated changes to future changes on the sub
  future_action_type?: string | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_original_unit_price?: number | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_new_unit_price?: number | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_original_item_price_id?: string | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_new_item_price_id?: string | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_original_plan_quantity?: number | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_new_plan_quantity?: number | null;
  /**
   * @deprecated Prefer `future_item_price_changes` (PLAN row when present).
   */
  future_reason?: string | null;
  // Snapshot of the subscription state after scheduled changes take effect (from future_sub)
  future_sub_status?: string | null;
  future_sub_plan_id?: string | null;
  future_sub_plan_amount?: number | null;
  future_sub_plan_quantity?: number | null;
  future_sub_plan_unit_price?: number | null;
  future_sub_billing_period?: number | null;
  future_sub_billing_period_unit?: string | null;
  future_sub_currency_code?: string | null;
  future_sub_item_price_id?: string | null;
  future_sub_item_unit_price?: number | null;
  future_sub_item_plan_amount?: number | null;
  future_sub_item_plan_quantity?: number | null;
  // Boolean update outcome columns (one pair non-null per job type)
  will_update_sub?: boolean | null;
  will_update_future_sub?: boolean | null;
  did_update_sub?: boolean | null;
  did_update_future_sub?: boolean | null;
  /** Present when simulation returns per line-item deltas (addons, etc.). */
  item_price_changes?: ItemPriceChangeRow[];
  future_item_price_changes?: ItemPriceChangeRow[];
};

export type GetSimulationSummaryResponse = {
  job_id: string;
  total_subs: number;
  modify_estimate: number;
  skip_estimate: number;
  no_op_estimate?: number;
};

export type GetSimulationResultsResponse = {
  job_id: string;
  subscription_updates: OptimizedSubscription[];
};

export type SimulationStateResponse = {
  optimization_state: {
    job: OptimizerJob;
    result: GetSimulationResultsResponse | null;
  };
  summary: GetSimulationSummaryResponse | null;
};

export type GetExecutionSummaryResponse = {
  total_jobs: number;
  arr_impact: number;
  impacted_subscriptions: number;
};

export type GetExecutionResultsResponse = {
  job_id: string;
  subscription_updates: OptimizedSubscription[];
  pagination?: Pagination;
};

export type ExecutionStateResponse = {
  job: OptimizerJob;
  result: {
    job_id: string;
    subscription_updates: OptimizedSubscription[];
  } | null;
};

// API argument types
type GetOptimizationsArgs = { appKey: string };
type GetOptimizationArgs = { appKey: string; optId: string };
type CreateOptimizationArgs = {
  appKey: string;
} & Partial<CustomizableOptimization>;
type DeleteOptimizationArgs = { appKey: string; optId: string };
type UpdateOptimizationArgs = {
  appKey: string;
  optId: string;
} & Partial<CustomizableOptimization>;
type GetSubscriptionsWithChangesArgs = {
  appKey: string;
  audience: RuleTopLevelCondition;
};
type GetCustomersForSubscriptionsArgs = {
  appKey: string;
  customerIds: string[];
};
type GetChangesForSubscriptionArgs = {
  appKey: string;
  subId: string;
};
type GetPlansArgs = {
  appKey: string;
};
type GetAddonsArgs = {
  appKey: string;
};
type CreateJobArgs = {
  appKey: string;
  optId: string;
  jobType: 'export' | 'simulate' | 'execute';
  cacheKey?: string;
};
type GetSimulationSummaryArgs = {
  appKey: string;
  jobId: string;
};
type GetSimulationResultsArgs = {
  appKey: string;
  jobId: string;
  page?: number;
  pageSize?: number;
};
type GetExecutionSummaryArgs = {
  appKey: string;
};
type GetExecutionResultsArgs = {
  appKey: string;
  jobId: string;
  page?: number;
  pageSize?: number;
};
type GetSimulationArgs = {
  appKey: string;
  optId: string;
};
type GetExecutionArgs = {
  appKey: string;
  optId: string;
};
type GetSimulationExportUrlArgs = {
  appKey: string;
  optId: string;
};
type GetExecutionExportUrlArgs = {
  appKey: string;
  optId: string;
};

export type CreateJobResponse = {
  ok: boolean;
  job_id: string;
  job_type: string;
};

export type GetSimulationExportUrlResponse = {
  url: string;
};

export type GetExecutionExportUrlResponse = {
  url: string;
};

export type OptimizerJob = {
  app_key: string;
  opt_id: string;
  job_id: string;
  job_ref_id?: string;
  job_type: EnumLike<typeof OptimizerJobType>;
  display_name?: string;
  created_at: DateString;
  updated_at: DateString;
  status?: EnumLike<typeof OptimizerJobStatus>;
  result?: string;
  cache_key?: string;
};

export type GetJobsResponse = {
  collection: OptimizerJob[];
};

type GetJobsArgs = {
  appKey: string;
  optId: string;
};

export class OptimizationSliceHelper {
  static cleanupActionFields(
    draft: WritableDraft<Partial<OptimizationAction>>
  ) {
    switch (draft.type) {
      case OptimizationActionType.PLAN_CHANGE: {
        const d = draft as WritableDraft<
          Partial<OptimizationActionPriceAdjustment> &
            Partial<OptimizationActionAddonPriceAdjustment> &
            Partial<OptimizationActionAlignToCatalog>
        >;
        delete d.adjustment_type;
        delete d.adjustment_value;
        delete d.scope;
        delete d.plan_adjustments;
        delete d.addons;
        delete d.item_price_ids;
        break;
      }
      case OptimizationActionType.PLAN_PRICE_ADJUSTMENT: {
        const d = draft as WritableDraft<
          Partial<OptimizationActionPlanChange> &
            Partial<OptimizationActionAddonPriceAdjustment> &
            Partial<OptimizationActionAlignToCatalog>
        >;
        delete d.plans;
        delete d.addons;
        delete d.item_price_ids;
        break;
      }
      case OptimizationActionType.ADDON_PRICE_ADJUSTMENT: {
        const d = draft as WritableDraft<
          Partial<OptimizationActionPlanChange> &
            Partial<OptimizationActionPriceAdjustment> &
            Partial<OptimizationActionAlignToCatalog>
        >;
        delete d.plans;
        delete d.plan_adjustments;
        delete d.item_price_ids;
        break;
      }
      case OptimizationActionType.ALIGN_TO_CATALOG: {
        const d = draft as WritableDraft<
          Partial<OptimizationActionPlanChange> &
            Partial<OptimizationActionPriceAdjustment> &
            Partial<OptimizationActionAddonPriceAdjustment>
        >;
        delete d.plans;
        delete d.adjustment_type;
        delete d.adjustment_value;
        delete d.currency_code;
        delete d.plan_adjustments;
        delete d.addons;
        break;
      }
      default:
        break;
    }
  }

  static createNewOptimization() {
    const newOptimization: Partial<Optimization> = {
      display_name: 'Renewal Optimization',
      audience: this.createNewAudience(),
      configurations: this.createNewConfigurations(),
    };
    return newOptimization;
  }

  static createNewAudience(): RuleTopLevelCondition {
    const newAudience = {
      all: [
        {
          fact: '',
          operator: '',
          value: '',
        },
      ],
    };

    return newAudience;
  }

  static createEmptyAudience(): RuleTopLevelCondition {
    const newAudience = {
      all: [],
    };

    return newAudience;
  }

  static createNewAction(): OptimizationAction {
    return {
      display_name: 'New rule',
      condition: this.createNewAudience(),
      type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
      scope: OptimizationPlanScope.ALL_PLANS,
      adjustment_type: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
      adjustment_value: 10,
    };
  }

  static createNewSimpleAction(): Partial<OptimizationAction> {
    return {
      display_name: 'Default rule',
      condition: this.createEmptyAudience(),
    };
  }

  static createNewConfigurations(): OptimizationConfigurations {
    return {
      apply_changes: ApplyChangesEnum.IMMEDIATELY,
      mid_cycle_billing: MidCycleBillingEnum.DISABLE_PRORATION,
      scheduled_changes_handling:
        ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS,
    };
  }

  static safeParse<T = unknown>(text: string, fallback?: T): T | undefined {
    if (!text) return fallback;
    try {
      return JSON.parse(text) as T;
    } catch (err) {
      GlobalLogger.error(err);
      return fallback;
    }
  }

  static createNewPlanChangePlan(): OptimizationActionPlanChange['plans'][number] {
    return {
      current_plan: '',
      target_plan: '',
    };
  }

  static createNewPlanAdjustment(): OptimizationPlanAdjustment {
    return {
      plan_id: '',
      adjustment_type: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
      adjustment_value: 10,
    };
  }

  static createNewAddonAdjustment(): OptimizationAddonAdjustment {
    return {
      item_price_id: '',
      adjustment_type: OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE,
      adjustment_value: 10,
    };
  }

  static createNewAlignToCatalogAction(
    action?: Pick<Partial<OptimizationAction>, 'display_name' | 'condition'>
  ): OptimizationAction {
    return {
      display_name: action?.display_name || 'New rule',
      condition: action?.condition ?? this.createNewAudience(),
      type: OptimizationActionType.ALIGN_TO_CATALOG,
      scope: OptimizationAlignToCatalogScope.ALL_ITEMS,
    };
  }

  static getPlanScope(
    action:
      | Pick<
          Partial<OptimizationActionPriceAdjustment>,
          'scope' | 'plan_adjustments'
        >
      | undefined
  ): OptimizationPlanScope {
    if (action?.scope === OptimizationPlanScope.SPECIFIC_PLANS) {
      return OptimizationPlanScope.SPECIFIC_PLANS;
    }
    if ((action?.plan_adjustments?.length ?? 0) > 0) {
      return OptimizationPlanScope.SPECIFIC_PLANS;
    }
    return OptimizationPlanScope.ALL_PLANS;
  }

  static getAddonScope(
    action:
      | Pick<
          Partial<OptimizationActionAddonPriceAdjustment>,
          'scope' | 'addons'
        >
      | undefined
  ): OptimizationAddonScope {
    if (action?.scope === OptimizationAddonScope.SPECIFIC_ADDONS) {
      return OptimizationAddonScope.SPECIFIC_ADDONS;
    }
    if ((action?.addons?.length ?? 0) > 0) {
      return OptimizationAddonScope.SPECIFIC_ADDONS;
    }
    return OptimizationAddonScope.ALL_ADDONS;
  }

  static normalizeAction(action: OptimizationAction): OptimizationAction {
    if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
      const scope = this.getPlanScope(action);
      const normalized: OptimizationAction = {
        ...action,
        scope,
      };
      if (scope === OptimizationPlanScope.ALL_PLANS) {
        delete normalized.plan_adjustments;
      } else {
        delete normalized.adjustment_type;
        delete normalized.adjustment_value;
      }
      return normalized;
    }

    if (action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT) {
      const scope = this.getAddonScope(action);
      const normalized: OptimizationAction = {
        ...action,
        scope,
      };
      if (scope === OptimizationAddonScope.ALL_ADDONS) {
        delete normalized.addons;
      } else {
        delete normalized.adjustment_type;
        delete normalized.adjustment_value;
      }
      return normalized;
    }

    if (action.type === OptimizationActionType.ALIGN_TO_CATALOG) {
      const scope =
        action.scope === OptimizationAlignToCatalogScope.SPECIFIC_ITEMS
          ? OptimizationAlignToCatalogScope.SPECIFIC_ITEMS
          : OptimizationAlignToCatalogScope.ALL_ITEMS;
      const normalized: OptimizationAction = {
        ...action,
        scope,
      };
      if (scope === OptimizationAlignToCatalogScope.ALL_ITEMS) {
        delete normalized.item_price_ids;
      }
      return normalized;
    }

    return action;
  }

  static normalizeActions(
    actions: OptimizationAction[] | undefined
  ): OptimizationAction[] | undefined {
    return actions?.map((action) => this.normalizeAction(action));
  }

  /**
   * Generates a deterministic MD5 hash cache key from optimization's audience, actions, and configurations.
   * This is used to detect when inputs have changed and a new job is needed.
   * @param optimization - The optimization object containing audience, actions, and configurations
   * @returns MD5 hash string of the combined inputs
   */
  static generateCacheKey(optimization: Partial<Optimization>): string {
    // Serialize each field separately in a fixed order to ensure deterministic output
    // This approach is more reliable than relying on object key ordering
    const audienceStr = JSON.stringify(optimization.audience ?? null);
    const actionsStr = JSON.stringify(optimization.actions ?? null);
    const configurationsStr = JSON.stringify(
      optimization.configurations ?? null
    );

    // Combine in fixed order
    const combinedString = `${audienceStr}|${actionsStr}|${configurationsStr}`;

    // Compute MD5 hash
    return md5(combinedString);
  }

  /**
   * Generates a unique display name for duplication by checking against existing optimizations.
   * If the original name is "Name", it will generate "Name (1)".
   * If the original name is already "Name (1)", it will generate "Name (2)".
   * Continues incrementing until a unique name is found.
   * @param originalName - The original display name to duplicate
   * @param existingOptimizations - Array of existing optimizations to check against
   * @returns A unique display name that doesn't conflict with existing optimizations
   */
  static generateUniqueDisplayName(
    originalName: string,
    existingOptimizations: Optimization[]
  ): string {
    if (!existingOptimizations || existingOptimizations.length === 0) {
      return `${originalName} (1)`;
    }

    const allDisplayNames = new Set(
      existingOptimizations.map((opt) => opt.display_name?.toLowerCase() || '')
    );

    // Check if the name matches pattern "Name (N)" where N is a number at the end
    const match = originalName.match(/^(.+?)\s+\((\d+)\)$/);
    let baseName: string;
    let startNumber: number;

    if (match) {
      // If it's already "Name (N)", use the base name and start from N+1
      baseName = match[1]!.trim();
      startNumber = parseInt(match[2]!, 10) + 1;
    } else {
      // If it's just "Name", use it as base and start from 1
      baseName = originalName;
      startNumber = 1;
    }

    // Find the first available number
    let number = startNumber;
    let candidateName: string;
    do {
      candidateName = `${baseName} (${number})`;
      number++;
    } while (allDisplayNames.has(candidateName.toLowerCase()));

    return candidateName;
  }
}

export const optimizationsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getOptimizations: build.query<
      OptimizationListResponse,
      GetOptimizationsArgs
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/optimizations`,
      providesTags: ['Optimizations'],
    }),
    getOptimization: build.query<Optimization, GetOptimizationArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}`,
      transformResponse: (
        raw: Omit<Optimization, 'audience' | 'actions' | 'configurations'> & {
          audience: string;
          actions: string;
          configurations: string;
        }
      ): Optimization => {
        const parsedActions = OptimizationSliceHelper.safeParse<
          Optimization['actions']
        >(raw.actions);

        return {
          ...raw,
          actions: OptimizationSliceHelper.normalizeActions(parsedActions),
          audience: OptimizationSliceHelper.safeParse<Optimization['audience']>(
            raw.audience,
            OptimizationSliceHelper.createNewAudience()
          ),
          configurations: OptimizationSliceHelper.safeParse<
            Optimization['configurations']
          >(raw.configurations),
        };
      },
      providesTags: (result, error, { optId }) => [
        { type: 'Optimizations', id: optId },
      ],
    }),
    createOptimization: build.mutation<
      CreateOptimizationResponse,
      CreateOptimizationArgs
    >({
      query: ({ appKey, ...body }) => {
        const normalizedActions = OptimizationSliceHelper.normalizeActions(
          body.actions
        );

        return {
          url: `/api/v2/apps/${appKey}/optimizations`,
          method: 'POST',
          body: {
            ...body,
            audience: JSON.stringify(body.audience),
            actions: JSON.stringify(normalizedActions),
            configurations: JSON.stringify(body.configurations),
          },
        };
      },
      invalidatesTags: ['Optimizations'],
    }),
    deleteOptimization: build.mutation<
      DeleteOptimizationResponse,
      DeleteOptimizationArgs
    >({
      query: ({ appKey, optId }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { optId }) => [
        'Optimizations',
        { type: 'Optimizations', id: optId },
      ],
    }),
    updateOptimization: build.mutation<
      UpdateOptimizationResponse,
      UpdateOptimizationArgs
    >({
      query: ({ appKey, optId, ...body }) => {
        const normalizedActions = OptimizationSliceHelper.normalizeActions(
          body.actions
        );

        return {
          url: `/api/v2/apps/${appKey}/optimizations/${optId}`,
          method: 'PUT',
          body: {
            ...body,
            audience: body.audience ? JSON.stringify(body.audience) : undefined,
            actions: normalizedActions
              ? JSON.stringify(normalizedActions)
              : undefined,
            configurations: body.configurations
              ? JSON.stringify(body.configurations)
              : undefined,
          },
        };
      },
      invalidatesTags: (result, error, { optId }) => [
        'Optimizations',
        { type: 'Optimizations', id: optId },
      ],
    }),
    getSubscriptionsWithChanges: build.query<
      GetSubscriptionsWithChangesResponse,
      GetSubscriptionsWithChangesArgs
    >({
      query: ({ appKey, audience }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/load-conflicting-subscriptions`,
        method: 'POST',
        body: {
          audience: JSON.stringify(audience),
        },
      }),
    }),
    getCustomersForSubscriptions: build.query<
      GetCustomersForSubscriptionsResponse,
      GetCustomersForSubscriptionsArgs
    >({
      query: ({ appKey, customerIds }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/preview-conflicting-subscriptions`,
        method: 'POST',
        body: {
          customer_ids: JSON.stringify(customerIds),
        },
      }),
    }),
    getChangesForSubscription: build.query<
      GetChangesForSubscriptionResponse,
      GetChangesForSubscriptionArgs
    >({
      query: ({ appKey, subId }) =>
        `/api/v2/apps/${appKey}/optimizations/preview-conflicting-subscriptions/details?sub_id=${subId}`,
    }),
    getPlans: build.query<GetPlansResponse['collection'], GetPlansArgs>({
      query: ({ appKey }) =>
        `/api/v2/apps/${appKey}/optimizations-chargebee/plans`,
      transformResponse: (res: GetPlansResponse) => res.collection,
    }),
    getAddons: build.query<GetAddonsResponse['collection'], GetAddonsArgs>({
      query: ({ appKey }) =>
        `/api/v2/apps/${appKey}/optimizations-chargebee/addons`,
      transformResponse: (res: GetAddonsResponse) => res.collection,
    }),
    createJob: build.mutation<CreateJobResponse, CreateJobArgs>({
      query: ({ appKey, optId, jobType, cacheKey }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}/jobs`,
        method: 'POST',
        body: {
          job_type: jobType,
          ...(cacheKey && { cache_key: cacheKey }),
        },
      }),
    }),
    cancelJob: build.mutation<
      void,
      { appKey: string; optId: string; jobId: string }
    >({
      query: ({ appKey, optId, jobId }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}/jobs/${jobId}/cancel`,
        method: 'POST',
      }),
    }),
    getJobs: build.query<GetJobsResponse, GetJobsArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/jobs`,
    }),
    getSimulationSummary: build.query<
      GetSimulationSummaryResponse,
      GetSimulationSummaryArgs
    >({
      query: ({ appKey, jobId }) =>
        `/api/v2/apps/${appKey}/optimizations/simulations/${jobId}/summary`,
    }),
    getSimulationResults: build.query<
      GetSimulationResultsResponse,
      GetSimulationResultsArgs
    >({
      query: ({ appKey, jobId, page, pageSize }) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page.toString());
        if (pageSize !== undefined)
          params.append('page_size', pageSize.toString());
        const queryString = params.toString();
        return `/api/v2/apps/${appKey}/optimizations/simulations/${jobId}${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getSimulation: build.query<SimulationStateResponse, GetSimulationArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/simulation`,
    }),
    getExecution: build.query<ExecutionStateResponse, GetExecutionArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/execution`,
    }),
    getSimulationExportUrl: build.query<
      GetSimulationExportUrlResponse,
      GetSimulationExportUrlArgs
    >({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/simulation/export`,
    }),
    getExecutionExportUrl: build.query<
      GetExecutionExportUrlResponse,
      GetExecutionExportUrlArgs
    >({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/execution/export`,
    }),
    getExecutionSummary: build.query<
      GetExecutionSummaryResponse,
      GetExecutionSummaryArgs
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/optimizations/executions`,
    }),
    getExecutionResults: build.query<
      GetExecutionResultsResponse,
      GetExecutionResultsArgs
    >({
      query: ({ appKey, jobId, page, pageSize }) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page.toString());
        if (pageSize !== undefined)
          params.append('page_size', pageSize.toString());
        const queryString = params.toString();
        return `/api/v2/apps/${appKey}/optimizations/executions/${jobId}${queryString ? `?${queryString}` : ''}`;
      },
    }),
  }),
});
